import React, { useContext, useEffect, useState } from 'react'; // Added useState
import { useNavigate, useLocation } from 'react-router-dom';

import styles from './ProfilePage.module.css';
import { 
  FaUser, FaHistory, FaMoon, FaSun, FaGift, FaSignOutAlt, 
  FaChevronRight, FaBell, FaHeadset, FaFileAlt 
} from 'react-icons/fa';

import { ThemeContext } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';

import DepositModal from '../../components/DepositModal'; // Import DepositModal
import WithdrawModal from '../../components/WithdrawModal'; // Import WithdrawModal


function ProfilePage() { // Removed openDepositModal, openWithdrawModal from props
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { user, logout, refreshUser, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Local state for modals
  const [isDepositModalOpen, setDepositModalOpen] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [initialTransaction, setInitialTransaction] = useState(null);

  const [isWithdrawModalOpen, setWithdrawModalOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (token) { // Only refresh if token is present
      refreshUser(token); // Pass the token to refreshUser
    }
  }, [refreshUser, token]); // Add token to dependency array

  useEffect(() => {
    const fetchUnreadNotifications = async () => {
      if (!user) return;
      try {
        const res = await api.get('/api/notifications');
        const unread = res.data.filter(n => !n.read).length;
        setUnreadCount(unread);
      } catch (error) {
        console.error('Failed to fetch notifications', error);
      }
    };

    fetchUnreadNotifications();
    const interval = setInterval(fetchUnreadNotifications, 60000);

    return () => clearInterval(interval);
  }, [user]);

  // Handle UPI redirection parameters
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const txnref = queryParams.get('txnref') || queryParams.get('txnid');

    if (txnref) {
      const fetchTransactionAndOpenModal = async () => {
        try {
          const { data } = await api.get(`/api/transactions/${txnref}`);
          if (data && data.txnStatus === 'Pending') {
            setDepositAmount(data.txnAmount);
            setInitialTransaction(data);
            setDepositModalOpen(true);
          }
        } catch (err) {
          console.error('Failed to fetch transaction from URL ref:', err);
        }
      };
      fetchTransactionAndOpenModal();
    }
  }, [location]);

  const handleLogout = () => {
    logout();
  };

  // Local modal open/close functions
  const openDepositModal = ({ amount = '' }) => {
    setDepositAmount(amount);
    setDepositModalOpen(true);
  };

  const closeDepositModal = () => {
    setDepositModalOpen(false);
    setDepositAmount('');
    setInitialTransaction(null);
    // Clear URL parameters if they exist
    if (new URLSearchParams(location.search).get('txnref') || new URLSearchParams(location.search).get('txnid')) {
        navigate(location.pathname, { replace: true });
    }
  };

  const openWithdrawModal = () => {
    setWithdrawModalOpen(true);
  };

  const closeWithdrawModal = () => {
    setWithdrawModalOpen(false);
  };

  const handleOpenDepositModal = () => {
    // Removed toggleSidebar call
    openDepositModal({});
  };

  const handleOpenWithdrawModal = () => {
    // Removed toggleSidebar call
    openWithdrawModal();
  };

  const handleNavigation = (path) => {
    // Removed toggleSidebar call
    navigate(path);
  };

  const formatCurrency = (amount, currency = 'INR') => {
    const number = Number(amount);
    const options = {
      style: 'currency',
      currency,
    };
    if (isNaN(number)) {
      return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', options).format(0);
    }
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', options).format(number);
  };

  const getInitials = (name) => {
    if (!name) return '';
    const words = name.split(' ');
    if (words.length > 1) {
      return words[0].charAt(0).toUpperCase() + words[1].charAt(0).toUpperCase();
    } else {
      return name.substring(0, 2).toUpperCase();
    }
  };

  return (
    <div className={styles['profile-page']}>
      <div className={styles['profile-header-bg']}></div>

      <div className={styles['user-info-section']}>
        <div className={styles['user-info-left']}>
          <div className={styles['user-initials']}>{getInitials(user?.name)}</div>
          <div className={styles['user-name-container']}>
            <div className={styles['user-name-row']}>
              <h3>{user?.name}</h3>
              <span className={styles['kyc-badge']}>Verified</span>
            </div>
            <p className={styles['client-id']}>Client ID: {user?._id?.substring(0, 8).toUpperCase() || 'N/A'}</p>
          </div>
        </div>
        <div className={styles['user-info-right']}>
          <p className={styles['user-phone']}>{user?.mobileNumber || 'N/A'}</p>
          <p className={styles['user-email']}>{user?.email}</p>
        </div>
      </div>

      <div className={styles['funds-section']}>
        <div className={styles.balance}>
          <p className={styles['bifurcated-fund-label']}>Total Trading Balance</p>
          <h2 className={styles['bifurcated-fund']}>{user ? formatCurrency(user.fund, 'INR') : 'Loading...'}</h2>
        </div>
        <div className={styles['fund-buttons']}>
          <button className={styles['add-funds-btn']} onClick={handleOpenDepositModal}>+ Add Funds</button>
          <button className={styles['withdraw-btn']} onClick={handleOpenWithdrawModal}>Withdraw</button>
        </div>
      </div>

      <div className={styles['scroll-container']}>
        {/* Account Section */}
        <div className={styles['menu-group']}>
          <h4 className={styles['group-title']}>Account & Portfolio</h4>
          <ul className={styles['account-options']}>
            <li onClick={() => handleNavigation('/account-details')}>
              <div className={styles['option-left']}>
                <div className={styles['icon-bg']}><FaUser /></div>
                <div className={styles['option-text']}>
                  <span className={styles['option-label']}>Account Details</span>
                  <span className={styles['option-sublabel']}>KYC, Bank accounts, Nominee</span>
                </div>
              </div>
              <FaChevronRight className={styles['arrow-icon']} />
            </li>

            <li onClick={() => handleNavigation('/notifications')}>
              <div className={styles['option-left']}>
                <div className={styles['icon-bg']}><FaBell /></div>
                <div className={styles['option-text']}>
                  <span className={styles['option-label']}>Notifications</span>
                  <span className={styles['option-sublabel']}>Alerts, Updates, Order status</span>
                </div>
                {unreadCount > 0 && <span className={styles.badge}>{unreadCount}</span>}
              </div>
              <FaChevronRight className={styles['arrow-icon']} />
            </li>

            <li onClick={() => handleNavigation('/fund-history')}>
              <div className={styles['option-left']}>
                <div className={styles['icon-bg']}><FaHistory /></div>
                <div className={styles['option-text']}>
                  <span className={styles['option-label']}>Fund History</span>
                  <span className={styles['option-sublabel']}>Deposits, Withdrawals, Ledger</span>
                </div>
              </div>
              <FaChevronRight className={styles['arrow-icon']} />
            </li>
          </ul>
        </div>

        {/* Security & Settings */}
        <div className={styles['menu-group']}>
          <h4 className={styles['group-title']}>Settings</h4>
          <ul className={styles['account-options']}>
            <li onClick={() => handleNavigation('/referral')}>
              <div className={styles['option-left']}>
                <div className={styles['icon-bg']}><FaGift /></div>
                <div className={styles['option-text']}>
                  <span className={styles['option-label']}>Refer and Earn</span>
                  <span className={styles['option-sublabel']}>Invite friends and earn rewards</span>
                </div>
              </div>
              <FaChevronRight className={styles['arrow-icon']} />
            </li>

            <li className={styles['theme-toggle-item']}>
              <div className={styles['option-left']}>
                <div className={styles['icon-bg']}><FaSun /></div>
                <div className={styles['option-text']}>
                  <span className={styles['option-label']}>Appearance</span>
                  <span className={styles['option-sublabel']}>{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
                </div>
              </div>
              <div className={styles['theme-toggle']}>
                <label className={styles.switch}>
                  <input
                    type="checkbox"
                    checked={theme === 'dark'}
                    onChange={toggleTheme}
                  />
                  <span className={styles.slider}>
                    <span className={styles['icon-container']}>
                      {theme === 'dark' ? <FaMoon /> : <FaSun />}
                    </span>
                  </span>
                </label>
              </div>
            </li>
          </ul>
        </div>

        {/* Support & Legal */}
        <div className={styles['menu-group']}>
          <h4 className={styles['group-title']}>Support & Help</h4>
          <ul className={styles['account-options']}>
            <li onClick={() => handleNavigation('/support')}>
              <div className={styles['option-left']}>
                <div className={styles['icon-bg']}><FaHeadset /></div>
                <div className={styles['option-text']}>
                  <span className={styles['option-label']}>Help Center</span>
                  <span className={styles['option-sublabel']}>Contact us, Raise a ticket</span>
                </div>
              </div>
              <FaChevronRight className={styles['arrow-icon']} />
            </li>
            <li onClick={() => handleNavigation('/legal')}>
              <div className={styles['option-left']}>
                <div className={styles['icon-bg']}><FaFileAlt /></div>
                <div className={styles['option-text']}>
                  <span className={styles['option-label']}>Legal & Compliance</span>
                  <span className={styles['option-sublabel']}>Terms, Privacy Policy</span>
                </div>
              </div>
              <FaChevronRight className={styles['arrow-icon']} />
            </li>
          </ul>
        </div>

        <div className={styles['sign-out-section-inline']}>
          <button className={styles['sign-out-btn']} onClick={handleLogout}>
            <FaSignOutAlt className={styles.icon} />
            <span>Sign Out</span>
          </button>
        </div>

        <div className={styles['footer-info']}>
          <p>{process.env.REACT_APP_NAME} v1.4.2</p>
        </div>
      </div>

      {isDepositModalOpen && (
        <DepositModal
          onClose={closeDepositModal}
          initialAmount={depositAmount}
          initialTransaction={initialTransaction}
        />
      )}
      {isWithdrawModalOpen && <WithdrawModal onClose={closeWithdrawModal} />}
    </div>
  );
}

export default ProfilePage;